import { useState, useMemo, useCallback, useEffect } from 'react';
import { Search, Plus, BookOpen, Tag, CalendarDays, Check, Menu, User, Building2 } from 'lucide-react';
import { FavoriteButton } from '@/components/FavoriteButton';
import { useNavigation } from '@/contexts/NavigationContext';
import { useAuth } from '@/contexts/AuthContext';
import { usePrompts } from '@/hooks/usePrompts';
import { useDirectResourcePermission, usePermissions, useResourcePermissions } from '@/hooks/usePermissions';
import { useProfile } from '@/hooks/useProfile';
import { useScopeOptions } from '@/hooks/useScopeOptions';
import { Skeleton } from '@/components/ui/skeleton';
// PromptFormModal replaced by full-page PromptFormPage
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { getProjectIdFromFilter } from '@/types/navigation';
import type { Tables } from '@/integrations/supabase/types';
import type { ScopeFilter } from '@/types/navigation';

type PromptOwner = Pick<Tables<'profiles'>, 'id' | 'nome' | 'email'>;

const PromptCard = ({
  prompt,
  onEdit,
  copiedId,
  onCopy,
  owner,
  currentUserId,
  isAdmin,
}: {
  prompt: Tables<'prompts'>;
  onEdit: (p: Tables<'prompts'>) => void;
  copiedId: string | null;
  onCopy: (p: Tables<'prompts'>) => void;
  owner?: PromptOwner;
  currentUserId?: string;
  isAdmin: boolean;
}) => {
  const { canEdit } = useResourcePermissions(prompt.owner_id, prompt.project_id ?? undefined);
  const { canEdit: canEditDirect } = useDirectResourcePermission('prompt', prompt.id);
  const canEditPrompt = canEdit || canEditDirect || isAdmin;
  const showOwner = !!currentUserId && prompt.owner_id !== currentUserId;
  const ownerLabel = owner?.nome || owner?.email || 'Usuário';

  return (
    <div className="bg-surface border border-color rounded-xl p-5 hover:border-brand-primary hover:-translate-y-1 hover:shadow-lg transition-all cursor-pointer flex flex-col h-full">
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-base font-semibold text-primary pr-2">{prompt.name}</h3>
        <div className="shrink-0">
          <FavoriteButton targetId={prompt.id} targetType="prompt" />
        </div>
      </div>
      <p className="text-sm text-secondary mb-4 line-clamp-2 flex-1">
        {prompt.description || 'Sem descrição'}
      </p>

      <div className="flex items-center justify-between text-xs text-tertiary mb-4">
        <span className="px-2 py-1 bg-secondary rounded flex items-center gap-1">
          <Tag className="w-3 h-3" />
          {prompt.category || 'Geral'}
        </span>
        <span className="flex items-center gap-1">
          <CalendarDays className="w-3 h-3" />
          {new Date(prompt.updated_at).toLocaleDateString('pt-BR')}
        </span>
      </div>

      {showOwner && (
        <div className="flex items-center gap-1.5 text-xs text-tertiary mb-4">
          <User className="w-3 h-3" />
          <span>Dono: <span className="text-secondary">{ownerLabel}</span></span>
        </div>
      )}

      <div className="flex gap-2 mt-auto">
        <button
          onClick={() => onCopy(prompt)}
          className={`flex-1 px-4 py-2 rounded-md text-sm font-medium flex items-center justify-center gap-2 transition-all ${
            copiedId === prompt.id
              ? 'bg-brand-primary/10 text-brand-primary border border-brand-primary/20'
              : 'brand-primary'
          }`}
        >
          {copiedId === prompt.id ? (
            <>
              <Check className="w-4 h-4 animate-scale-in" />
              Copiado!
            </>
          ) : (
            'Usar'
          )}
        </button>
        {canEditPrompt && (
          <button
            onClick={() => onEdit(prompt)}
            className="px-4 py-2 border border-color rounded-md text-sm text-primary hover:bg-hover"
          >
            Editar
          </button>
        )}
      </div>
    </div>
  );
};

export const PromptsPage = () => {
  const { navigation, sidebarCollapsed, setSidebarCollapsed, scopeFilter, setScopeFilter, searchPrefill, setSearchPrefill, setEditingResource } = useNavigation();
  const { user } = useAuth();
  const { canCreate } = usePermissions();
  const { isAdmin } = useProfile();
  const scopeOptions = useScopeOptions();

  const projectId = navigation.projectId;

  // Fetch all relevant scopes
  const { data: globalPrompts = [], isLoading: loadingGlobal, error: errorGlobal } = usePrompts('global', undefined, projectId);
  const { data: personalPrompts = [], isLoading: loadingPersonal, error: errorPersonal } = usePrompts('personal');

  const isLoading = loadingGlobal || loadingPersonal;
  const error = errorGlobal || errorPersonal;

  // Deduplicate
  const allPrompts = useMemo(() => {
    const map = new Map<string, Tables<'prompts'>>();
    globalPrompts.forEach(p => map.set(p.id, p));
    personalPrompts.forEach(p => map.set(p.id, p));
    return Array.from(map.values());
  }, [globalPrompts, personalPrompts]);

  const ownerIds = useMemo(
    () => Array.from(new Set(allPrompts.map(p => p.owner_id).filter(id => id && id !== user?.id))),
    [allPrompts, user?.id]
  );

  const { data: owners = [] } = useQuery({
    queryKey: ['prompt-owners', ownerIds],
    enabled: ownerIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, nome, email')
        .in('id', ownerIds);
      if (error) throw error;
      return data ?? [];
    },
  });

  const ownersById = useMemo(() => new Map(owners.map(owner => [owner.id, owner])), [owners]);

  const [search, setSearch] = useState('');

  // Consume searchPrefill from command palette
  useEffect(() => {
    if (searchPrefill) {
      setSearch(searchPrefill);
      setSearchPrefill('');
    }
  }, [searchPrefill, setSearchPrefill]);
  // Modal state no longer needed - using full-page form
  

  const [copiedPromptId, setCopiedPromptId] = useState<string | null>(null);
  const handleCopyPrompt = useCallback(async (prompt: Tables<'prompts'>) => {
    await navigator.clipboard.writeText(prompt.content);
    setCopiedPromptId(prompt.id);
    toast.success(`"${prompt.name}" copiado para a área de transferência`);
    setTimeout(() => setCopiedPromptId(null), 1500);
  }, [toast]);

  const filtered = useMemo(() => {
    let list = allPrompts;
    const filterProjectId = getProjectIdFromFilter(scopeFilter);

    if (filterProjectId) {
      list = list.filter(p => p.project_id === filterProjectId);
    } else if (scopeFilter === 'global') {
      list = list.filter(p => p.scope_type === 'global' && !p.project_id);
    } else if (scopeFilter === 'personal') {
      list = list.filter(p => p.scope_type === 'personal' && !p.project_id);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        p => p.name.toLowerCase().includes(q) || p.description?.toLowerCase().includes(q) || p.category?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [allPrompts, scopeFilter, search]);

  const openCreate = () => {
    setEditingResource({ type: 'prompt' });
  };

  const openEdit = (prompt: Tables<'prompts'>) => {
    setEditingResource({ type: 'prompt', id: prompt.id });
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="px-8 py-6 border-b border-color shrink-0">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            {sidebarCollapsed && (
              <button
                onClick={() => setSidebarCollapsed(false)}
                className="p-2 hover:bg-hover rounded-md"
              >
                <Menu className="w-5 h-5" />
              </button>
            )}
            <BookOpen className="w-5 h-5 text-primary" />
            <h1 className="text-xl font-semibold text-primary">Biblioteca de Prompts</h1>
          </div>
          {canCreate && (
            <button
              onClick={openCreate}
              className="px-4 py-2 brand-primary rounded-md text-sm font-medium flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Novo Prompt
            </button>
          )}
        </div>

        {/* Scope filter tabs */}
        <div className="flex items-center gap-1 mb-4">
          {scopeOptions.map((scope) => (
            <button
              key={scope.id}
              onClick={() => setScopeFilter(scope.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all flex items-center gap-1.5 ${
                scopeFilter === scope.id
                  ? 'bg-brand-primary text-white border-transparent'
                  : 'border-color text-secondary hover:bg-hover'
              }`}
            >
              {scope.icon}
              {scope.label}
            </button>
          ))}
        </div>

        <p className="text-sm text-secondary mb-4">
          {isLoading ? '...' : `${filtered.length} prompt${filtered.length !== 1 ? 's' : ''} encontrado${filtered.length !== 1 ? 's' : ''}`}
        </p>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tertiary" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar prompts..."
            className="w-full h-10 pl-10 pr-4 rounded-md border border-color bg-surface text-sm text-primary placeholder:text-tertiary focus:outline-none focus:ring-2 focus:ring-brand-primary"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-8">
        {error && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <BookOpen className="w-10 h-10 text-tertiary mb-4" />
            <h3 className="text-lg font-semibold text-primary mb-2">Erro ao carregar prompts</h3>
            <p className="text-sm text-secondary">{(error as Error).message}</p>
          </div>
        )}

        {isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="border border-color rounded-xl p-5 space-y-3">
                <Skeleton className="w-10 h-10 rounded-lg" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-2/3" />
                <Skeleton className="h-9 w-full rounded-md" />
              </div>
            ))}
          </div>
        )}

        {!isLoading && !error && filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <BookOpen className="w-10 h-10 text-tertiary mb-4" />
            <h3 className="text-lg font-semibold text-primary mb-2">
              {allPrompts.length === 0 ? 'Nenhum prompt cadastrado' : 'Nenhum resultado'}
            </h3>
            <p className="text-sm text-secondary mb-6">
              {allPrompts.length === 0
                ? 'Crie seu primeiro prompt para começar.'
                : 'Tente ajustar a busca ou os filtros.'}
            </p>
            {allPrompts.length === 0 && canCreate && (
              <button
                onClick={openCreate}
                className="px-4 py-2 brand-primary rounded-md text-sm font-medium flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Novo Prompt
              </button>
            )}
          </div>
        )}

        {!isLoading && !error && filtered.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map(prompt => (
              <PromptCard
                key={prompt.id}
                prompt={prompt}
                onEdit={openEdit}
                copiedId={copiedPromptId}
                onCopy={handleCopyPrompt}
                owner={ownersById.get(prompt.owner_id)}
                currentUserId={user?.id}
                isAdmin={isAdmin}
              />
            ))}
          </div>
        )}
      </div>

    </div>
  );
};
